/** @jsxImportSource @emotion/react */
import React from 'react';
import dayjs from 'dayjs';
import { Box, Stack, IconButton } from '@mui/material';
import { css } from '@emotion/react';
import { CellBooking } from '../../../../types/meetingRoomTable';
import { useTelegram } from '../../../../hooks/useTelegram';
import RepeatIcon from '../../../../assets/repeatIcon';
import EditIcon from '../../../../assets/editIcon';
import RemoveIcon from '../../../../assets/removeIcon';
import { CellWrap, EmptyCell, BookedCell, BlockedCell, BookingText } from './styles';
import { calculateHeightPercentage, getRepeatValueLabel, parseBookingTime } from './helpers';
import { MeetingRoomBooking } from '../../../../types';

type MeetingRoomCellProps = {
    type: 'empty' | 'booked' | 'blocked';
    cellStart: dayjs.Dayjs;
    bookings: CellBooking[];
    userOtherBookings: Array<{ booking: CellBooking; user?: CellBookingUser }>;
    onClick: (emptyCellStartTime?: string, emptyCellEndTime?: string) => void;
    editable?: boolean;
    onEditClick?: (
        booking: MeetingRoomBooking,
        meetingRoomName?: string,
        telegramUserId?: number,
    ) => void;
    onDeleteClick?: (bookingId: string) => void;
    hideTopBorder?: boolean;
    hideBottomBorder?: boolean;
};

const MeetingRoomCell: React.FC<MeetingRoomCellProps> = ({
    type,
    cellStart,
    bookings,
    userOtherBookings,
    onClick,
    editable,
    onEditClick,
    onDeleteClick,
    hideTopBorder,
    hideBottomBorder,
}) => {
    const { colorScheme } = useTelegram();
    const cellEnd = cellStart.add(1, 'hour');
    const hasBothBookingsAndBlocked = bookings.length > 0 && userOtherBookings.length > 0;

    // Empty cell
    if (!hasBothBookingsAndBlocked && type === 'empty') {
        return (
            <CellWrap
                colorScheme={colorScheme}
                hideTopBorder={hideTopBorder}
                hideBottomBorder={hideBottomBorder}
            >
                <EmptyCell colorScheme={colorScheme} onClick={() => onClick?.()} />
            </CellWrap>
        );
    }

    // Separate continuing bookings from new bookings
    const newBookings = bookings.filter((b) => !b.isContinuing);
    const continuingBookings = bookings.filter((b) => b.isContinuing);

    // Convert bookings segments so it can be properly rendered in the cell
    // Only render content for new bookings (not continuing ones)
    const bookedSegments: TimeSegment[] = newBookings.map((booking) => {
        const rawStart = parseBookingTime(booking, 'startTime', cellStart);
        const rawEnd = parseBookingTime(booking, 'endTime', cellStart);

        return {
            type: 'booking',
            start: rawStart.isBefore(cellStart) ? cellStart : rawStart,
            end: rawEnd.isAfter(cellEnd) ? cellEnd : rawEnd,
            booking,
            user: booking.userData,
        };
    });

    // Add continuing bookings as filler segments (no content, just fill space)
    continuingBookings.forEach((booking) => {
        const rawEnd = parseBookingTime(booking, 'endTime', cellStart);
        bookedSegments.push({
            type: 'continuing',
            start: cellStart,
            end: rawEnd.isAfter(cellEnd) ? cellEnd : rawEnd,
            booking,
            user: booking.userData,
        });
    });

    // Add blocked bookings to show user's bookings in OTHER rooms as blocked
    if (hasBothBookingsAndBlocked) {
        const sortedBookings = bookings.sort(
            (a, b) =>
                parseBookingTime(a, 'startTime', cellStart).valueOf() -
                parseBookingTime(b, 'startTime', cellStart).valueOf(),
        );

        userOtherBookings.forEach((userOtherBooking) => {
            const rawUserOtherBookingStart = parseBookingTime(
                userOtherBooking.booking,
                'startTime',
                cellStart,
            );
            const rawOtherUserBookingEnd = parseBookingTime(
                userOtherBooking.booking,
                'endTime',
                cellStart,
            );
            // Link blocked time to cell boundaries
            let currentStart = rawUserOtherBookingStart;

            sortedBookings.forEach((booking) => {
                const bookingStart = parseBookingTime(booking, 'startTime', cellStart);
                const bookingEnd = parseBookingTime(booking, 'endTime', cellStart);

                if (currentStart.isBefore(bookingStart)) {
                    bookedSegments.push({
                        type: 'blocked',
                        start: currentStart,
                        end: bookingStart,
                        booking: userOtherBooking.booking,
                        user: userOtherBooking.user,
                    });
                }
                if (bookingEnd.isAfter(currentStart)) {
                    currentStart = bookingEnd;
                }
            });

            if (currentStart.isBefore(rawOtherUserBookingEnd)) {
                bookedSegments.push({
                    type: 'blocked',
                    start: currentStart,
                    end: rawOtherUserBookingEnd,
                    booking: userOtherBooking.booking,
                    user: userOtherBooking.user,
                });
            }
        });
    } else if (type === 'blocked') {
        userOtherBookings
            .sort((a, b) =>
                parseBookingTime(a.booking, 'startTime', cellStart).diff(
                    parseBookingTime(b.booking, 'startTime', cellStart),
                ),
            )
            .forEach((userOtherBooking) => {
                const rawStart = parseBookingTime(userOtherBooking.booking, 'startTime', cellStart);
                const rawEnd = parseBookingTime(userOtherBooking.booking, 'endTime', cellStart);

                bookedSegments.push({
                    type: 'blocked',
                    start: rawStart.isBefore(cellStart) ? cellStart : rawStart,
                    end: rawEnd.isAfter(cellEnd) ? cellEnd : rawEnd,
                    booking: userOtherBooking.booking,
                    user: userOtherBooking.user,
                });
            });
    }

    // Sort all booked segments by start time
    bookedSegments.sort((a, b) => a.start.diff(b.start));

    // Generate React elements for the cell by iterating through booked segments
    // Gaps (empty clickable areas) are inserted between segments to fill the hour
    const cellContent: React.ReactNode[] = [];
    // Tracks the end time of the last rendered segment to calculate gaps between segments
    let lastSegmentRendered = cellStart;

    bookedSegments.forEach((segment) => {
        // Add gap to move booking to the bottom
        // eg. booking 8:45-9:00
        if (segment.start.isAfter(lastSegmentRendered)) {
            const gapStart = lastSegmentRendered.format('HH:mm:ss');
            const gapEnd = segment.start.format('HH:mm:ss');

            cellContent.push(
                <EmptyCell
                    colorScheme={colorScheme}
                    key={`gap-${gapStart}`}
                    onClick={() => onClick(gapStart, gapEnd)}
                    style={{
                        height: `${calculateHeightPercentage(lastSegmentRendered, segment.start)}%`,
                    }}
                />,
            );
        }

        const fillPercentage = calculateHeightPercentage(segment.start, segment.end);

        if (segment.type === 'booking') {
            const repeatValue = getRepeatValueLabel(segment.booking.repeatValue);
            const isShortBooking = segment.end.diff(segment.start, 'minute') <= 15;

            // Get the original booking times to display full duration if it spans multiple cells
            const rawStart = parseBookingTime(segment.booking, 'startTime', cellStart);
            const rawEnd = parseBookingTime(segment.booking, 'endTime', cellStart);

            cellContent.push(
                <BookedCell
                    colorScheme={colorScheme}
                    fillPercentage={fillPercentage}
                    key={`${segment.booking.meetingRoom?.id}-${segment.start.format('HH:mm')}`}
                >
                    <Stack
                        height="100%"
                        width="100%"
                        flexDirection={isShortBooking || editable ? 'row' : 'column'}
                    >
                        <BookingText>
                            {isShortBooking ? (
                                `${segment.user?.name} ${segment.user?.surname}, ${rawStart.format(
                                    'HH:mm',
                                )} - ${rawEnd.format('HH:mm')}`
                            ) : (
                                <>
                                    <BookingText>
                                        {segment.user?.name} {segment.user?.surname}
                                    </BookingText>
                                    <BookingText>
                                        {rawStart.format('HH:mm')} - {rawEnd.format('HH:mm')}
                                    </BookingText>
                                </>
                            )}
                        </BookingText>
                        {repeatValue && (
                            <BookingText
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    ...(editable && {
                                        alignItems: 'start',
                                        justifyContent: 'flex-end',
                                    }),
                                }}
                            >
                                <RepeatIcon
                                    color="#FFFFFF"
                                    repeatIconStyles={css({
                                        fontSize: '10px',
                                        marginRight: '3px',
                                    })}
                                />
                                {repeatValue}
                            </BookingText>
                        )}
                        {editable && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'start',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEditClick?.(
                                            {
                                                ...segment.booking,
                                                spaceId: segment.booking.meetingRoom?.id || '',
                                                userId: String(segment.user?.telegramUserId || ''),
                                            } as MeetingRoomBooking,
                                            segment.booking.meetingRoom?.name,
                                            segment.user?.telegramUserId,
                                        );
                                    }}
                                    sx={{ padding: '0 2px 2px 2px' }}
                                >
                                    <EditIcon
                                        editIconStyles={css({
                                            fontSize: '10px',
                                            width: '10px',
                                            height: '10px',
                                        })}
                                    />
                                </IconButton>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteClick?.(segment.booking.id);
                                    }}
                                    sx={{ padding: '0 2px 2px 2px' }}
                                >
                                    <RemoveIcon
                                        removeIconStyles={css({
                                            fontSize: '10px',
                                            width: '10px',
                                            height: '10px',
                                        })}
                                    />
                                </IconButton>
                            </Box>
                        )}
                    </Stack>
                </BookedCell>,
            );
        } else if (segment.type === 'continuing') {
            // Continuing booking - show just the background color without borders or content
            cellContent.push(
                <BookedCell
                    colorScheme={colorScheme}
                    fillPercentage={fillPercentage}
                    isContinuing
                    key={`continuing-${segment.booking.id}-${segment.start.format('HH:mm')}`}
                />,
            );
        } else {
            cellContent.push(
                <BlockedCell
                    key={`blocked-${segment.start.format('HH:mm')}`}
                    style={{ height: `${fillPercentage}%` }}
                />,
            );
        }

        lastSegmentRendered = segment.end;
    });

    // Fill remaining time at the end of the hour with a clickable empty gap
    // This allows users to book time after the last booking/blocked segment
    if (lastSegmentRendered.isBefore(cellEnd)) {
        const gapStart = lastSegmentRendered.format('HH:mm:ss');
        const gapEnd = cellEnd.format('HH:mm:ss');

        cellContent.push(
            <EmptyCell
                colorScheme={colorScheme}
                key={`gap-end-${gapStart}`}
                onClick={() => onClick(gapStart, gapEnd)}
                style={{ height: `${calculateHeightPercentage(lastSegmentRendered, cellEnd)}%` }}
            />,
        );
    }

    return (
        <CellWrap
            colorScheme={colorScheme}
            hideTopBorder={hideTopBorder}
            hideBottomBorder={hideBottomBorder}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                {cellContent}
            </Box>
        </CellWrap>
    );
};

export default MeetingRoomCell;

type TimeSegment = {
    type: 'booking' | 'blocked' | 'continuing';
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    booking: CellBooking;
    user?: CellBookingUser;
};

type CellBookingUser = {
    name: string;
    surname: string;
    telegramUserId: number;
};
